import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ImageFileUpload, postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function UpdateTeamMember() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [formData, setFormData] = useState({
    teamMemberName: "",
    designation: "",
    description: "",
    memberType: "KP Startup Hub Board Members",
    image: null,
    order: 1,
    existingImage: "",
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (state?.memberData) {
      const { teamMemberName, designation, description, memberType, image, order } = state.memberData;

      setFormData({
        teamMemberName: teamMemberName || "",
        designation: designation || "",
        description: description || "",
        memberType: memberType || "KP Startup Hub Board Members",
        image: null,
        order: order || 1,
        existingImage: image || "",
      });

      if (image) {
        setImagePreview(image);
      }
    }
  }, [state]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const uploadImage = async () => {
    if (!formData.image) return formData.existingImage;

    const imageFormData = new FormData();
    imageFormData.append("file", formData.image);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);

      if (response?.success) {
        return response?.url || response.data?.imageUrl || response.data?.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Error uploading image");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.teamMemberName.trim() || !formData.designation.trim() || !formData.description.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);

    try {
      // Upload image if a new one was selected
      let imageUrl = formData.existingImage;
      if (formData.image) {
        const uploadedImageUrl = await uploadImage();
        if (!uploadedImageUrl) {
          setLoading(false);
          return;
        }
        imageUrl = uploadedImageUrl;
      }

      // Prepare the data to send
      const payload = {
        teamMemberName: formData.teamMemberName,
        designation: formData.designation,
        description: formData.description,
        memberType: formData.memberType,
        image: imageUrl,
        order: parseInt(formData.order),
        id: state?.memberData?._id,
      };

      const response = await postData("api/team-members/update", payload);

      if (response?.success) {
        toast.success("Team member updated successfully!");
        navigate("/team-member-list");
      } else {
        throw new Error(response?.message || "Failed to update team member");
      }
    } catch (error) {
      console.error("Update team member error:", error);
      toast.error(error.response?.data?.message || error.message || "Error updating team member");
    } finally {
      setLoading(false);
    }
  };

  const memberTypeOptions = [
    { value: "KP Startup Hub Board Members", label: "KP Startup Hub Board Members" },
    { value: "Operational Team", label: "Operational Team" },
    { value: "IT Team", label: "IT Team" },
  ];

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Team Member Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Team Member</li>
                  <li className="breadcrumb-item active">Update Team Member</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/team-member-list")}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Update Team Member</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Team Member Name */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="teamMemberName" className="form-label fw-medium">
                          Team Member Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="teamMemberName"
                          name="teamMemberName"
                          className="form-control"
                          placeholder="Enter team member name"
                          value={formData.teamMemberName}
                          onChange={handleInputChange}
                          required
                          minLength={2}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Designation */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="designation" className="form-label fw-medium">
                          Designation <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="designation"
                          name="designation"
                          className="form-control"
                          placeholder="Enter designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          required
                          minLength={2}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Member Type */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="memberType" className="form-label fw-medium">
                          Member Type <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="memberType"
                          name="memberType"
                          className="form-control"
                          value={formData.memberType}
                          onChange={handleInputChange}
                          required
                        >
                          {memberTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={4}
                          placeholder="Enter description..."
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Image */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Member Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 400x400px, Max 2MB (JPEG/PNG)
                        </small>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: "50%" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="order" className="form-label fw-medium">
                          Display Order
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="number"
                          id="order"
                          name="order"
                          className="form-control"
                          placeholder="Enter display order"
                          value={formData.order}
                          onChange={handleInputChange}
                          min="1"
                        />
                        <small className="text-muted">Lower numbers appear first</small>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Team Member"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
